import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import RightInfoSidebar from '@uikit/old/RightInfoSidebar/RightInfoSidebar';
import Loader from '@uikit/old/Loader/Loader';
import ProgressBar from '@uikit/old/ProgressBar/ProgressBar';
import Input from '@uikit/Form/Input/Input';
import { Button } from '@uikit/Form/Button/Button';
import MessageThread from '@components/shared/MessageThread/MessageThread';
import { ThreadAttachmentType, ThreadMessageType } from '@ts/messageThread.types';
import AttachFileZone from '@components/shared/AttachFileZone/AttachFileZone';
import PlacementDetails from '@components/Publisher/Orders/OrdersTable/_components/PlacementDetails/PlacementDetails';
import SubmitNewLiveUrl from '@components/Publisher/Orders/OrdersTable/_components/ChangeRequestSidebar/_components/SubmitNewLiveUrl/SubmitNewLiveUrl';
import {
  changeRequestAreaList,
  changeRequestStatusConfig,
} from '@components/Publisher/Orders/OrdersTable/constants';

import { addPublisherChangeRequestMessageApi, submitNewLiveLinkApi } from '@api/publishers.api';
import { getFileByUidApi } from '@api/fileImport.api';
import useOrderChangeRequest from '@hooks/useOrderChangeRequest';
import useMessageAttachments from '@hooks/useMessageAttachments';
import { addNotification } from '@redux/actions/notifications.actions';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import { getBlobFromBase64 } from '@utils/createFileFromBytesArray';
import downloadFile from '@utils/downloadCSV';
import { getDateShort } from '@utils/date';
import {
  ChangeRequestMessageType,
  ChangeRequestType,
  PublishersOrderType,
} from '@ts/publishers.types';
import {
  ChangeRequestAuthorType,
  ChangeRequestStatus,
} from 'respona_api/generated/order-publisher_pb';

import './ChangeRequestSidebar.scss';

type ChangeRequestSidebarPropsType = {
  isOpen: boolean;
  handleClose: () => void;
  data: PublishersOrderType;
  onChangeRequestUpdated?: (changeRequest: ChangeRequestType) => void;
};

// Internal (Respona-team-only) notes are stripped server-side; this is defence in depth so a leaked
// one could never render under the "Respona Team" label with its body and attachments.
const toThreadMessages = (messages: ChangeRequestMessageType[]): ThreadMessageType[] =>
  messages
    .filter((message) => !message.internalNote)
    .map((message) => ({
      id: message.id,
      authorName: message.authorName,
      isResponaTeam:
        message.authorType === ChangeRequestAuthorType.CHANGE_REQUEST_AUTHOR_TYPE_RESPONA_TEAM,
      createdAt: message.createdAt,
      body: message.body,
      attachedFiles: message.attachedFilesList,
    }));

function ChangeRequestSidebar({
  isOpen,
  handleClose,
  data,
  onChangeRequestUpdated,
}: ChangeRequestSidebarPropsType): JSX.Element {
  const dispatch = useDispatch();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const [replyBody, setReplyBody] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [downloadingFileUid, setDownloadingFileUid] = useState<string>(null);

  const {
    item: changeRequest,
    isLoading,
    isError,
    refetch,
    update: updateChangeRequestCache,
  } = useOrderChangeRequest(isOpen && data ? data.id : -1);

  const {
    attachments,
    isUploading,
    uploadingProgress,
    handleAttachmentChange,
    reset: resetAttachments,
    resetToken: attachmentsResetToken,
  } = useMessageAttachments(isOpen ? data?.id : null);

  // Keyed on the id, not the object: the parent now derives `data` from the rows
  // cache, so its identity changes on every refetch and would wipe a draft reply.
  useEffect(() => {
    if (isOpen) {
      setReplyBody('');
    }
  }, [isOpen, data?.id]);

  if (!isOpen || !data) {
    return null;
  }

  const isResolved = changeRequest?.status === ChangeRequestStatus.CHANGE_REQUEST_RESOLVED;
  const showComposer = !!changeRequest && !isResolved;
  const isSendDisabled = !replyBody.trim().length || isSendingMessage || isUploading;

  // Prefer the freshly fetched change request; fall back to the row's overlay field
  // so the header pill is right on the very first paint, before the detail lands.
  const headerStatus = changeRequest?.status ?? data.optionalChangeRequestStatus;
  const statusConfig = changeRequestStatusConfig[headerStatus];

  const changeAreas = changeRequestAreaList.filter((item) =>
    changeRequest?.areasList?.includes(item.value)
  );

  const handleSendMessage = () => {
    if (isSendDisabled || !changeRequest) {
      return;
    }

    setIsSendingMessage(true);

    addPublisherChangeRequestMessageApi(
      workspaceId,
      changeRequest.id,
      replyBody.trim(),
      attachments
    )
      .then((updatedChangeRequest) => {
        updateChangeRequestCache(data.id, updatedChangeRequest);
        onChangeRequestUpdated?.(updatedChangeRequest);
        setReplyBody('');
        resetAttachments();
      })
      .catch((err) => {
        console.error(err);
        dispatch(addNotification({ title: 'Message is not sent. Try again', type: 'error' }));
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  };

  const handleSubmitNewLiveUrl = (newLiveUrl: string): Promise<void> =>
    submitNewLiveLinkApi(workspaceId, changeRequest.id, newLiveUrl)
      .then((updatedChangeRequest) => {
        updateChangeRequestCache(data.id, updatedChangeRequest);
        onChangeRequestUpdated?.(updatedChangeRequest);
        dispatch(
          addNotification({ title: 'New live URL submitted for approval', type: 'success' })
        );
      })
      .catch((err) => {
        console.error(err);
        dispatch(addNotification({ title: 'New URL is not submitted. Try again', type: 'error' }));
        // Re-throw so the panel keeps the draft open for a retry.
        throw err;
      });

  const handleReplyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    handleSendMessage();
  };

  const handleDownloadAttachment = (file: ThreadAttachmentType) => {
    if (downloadingFileUid) {
      return;
    }

    setDownloadingFileUid(file.uid);

    getFileByUidApi(file.uid, workspaceId)
      .then(({ fileBytes }) => {
        if (!fileBytes) {
          throw new Error('File not found');
        }

        const fileBlob = getBlobFromBase64(fileBytes, file.type);
        downloadFile(file.name, fileBlob);
      })
      .catch((err) => {
        console.error(err);
        dispatch(addNotification({ title: 'File is not downloaded. Try again', type: 'error' }));
      })
      .finally(() => {
        setDownloadingFileUid(null);
      });
  };

  const renderBody = () => {
    if (isLoading) {
      return <Loader isLoading />;
    }

    if (isError || !changeRequest) {
      return (
        <div className="dropdown-backlinks__container-error" data-testid="change-request-error">
          <div className="dropdown-backlinks__container-error__text">
            We couldn’t load this change request.
          </div>
          <Button type="ghost" onClick={() => refetch()} data-testid="change-request-retry">
            Try again
          </Button>
        </div>
      );
    }

    return (
      <div className="dropdown-backlinks__container chat-content">
        <div className="dropdown-backlinks__container-body">
          {isResolved && (
            <div
              className="dropdown-backlinks__container__notification success"
              data-testid="change-request-resolved"
            >
              <div className="dropdown-backlinks__container__notification-icon">
                <SVGIcon icon="successCircleFilled" color="#23E771" size={34} />
              </div>
              <div className="dropdown-backlinks__container__notification-wrapper">
                <div className="dropdown-backlinks__container__notification-title">
                  Change request resolved
                </div>
                <div className="dropdown-backlinks__container__notification-content">
                  Placement back to Live · {getDateShort(new Date(changeRequest.updatedAt))}
                </div>
              </div>
            </div>
          )}
          {changeAreas.length > 0 && (
            <div className="dropdown-backlinks__container-info">
              WHAT NEEDS CHANGING
              {changeAreas.map((item) => (
                <div
                  key={item.value}
                  className="dropdown-backlinks__container-list-item"
                  data-testid={`change-request-area-${item.value}`}
                >
                  {item.icon === 'T' ? (
                    'T'
                  ) : (
                    <SVGIcon icon={item.icon} size={item.iconSize} color={item.iconColor} />
                  )}
                  <div>{item.title}</div>
                </div>
              ))}
            </div>
          )}
          <MessageThread
            messages={toThreadMessages(changeRequest.messagesList)}
            downloadingFileUid={downloadingFileUid}
            onDownloadAttachment={handleDownloadAttachment}
          />
        </div>
        {showComposer ? (
          <div className="dropdown-backlinks__container-composer">
            <div className="dropdown-backlinks__container-composer__row">
              <Input
                placeholder="Write a reply..."
                data-testid="change-request-reply-text"
                value={replyBody}
                onChange={(event) => setReplyBody(event.target.value)}
                onKeyDown={handleReplyKeyDown}
              />
              <Button
                leftIcon="sendArrow2"
                onClick={handleSendMessage}
                isLoading={isSendingMessage}
                disabled={isSendDisabled}
                data-testid="change-request-reply-submit"
              />
            </div>
            <div className="dropdown-backlinks__container-composer__attachments">
              <AttachFileZone key={attachmentsResetToken} onChange={handleAttachmentChange} />
              {isUploading ? (
                <ProgressBar title="Uploading attachment" percentage={uploadingProgress} />
              ) : null}
            </div>
            {/* Keyed on the order id so a draft URL never leaks between orders. */}
            <SubmitNewLiveUrl key={data.id} onSubmit={handleSubmitNewLiveUrl} />
          </div>
        ) : (
          <div
            className="dropdown-backlinks__container-read-only"
            data-testid="change-request-read-only"
          >
            This change request is resolved. The conversation is read-only.
          </div>
        )}
      </div>
    );
  };

  return (
    <RightInfoSidebar
      zIndex={1000}
      isOpen={isOpen}
      onClose={handleClose}
      testId="change-request-sidebar"
      classNames={{
        body: 'dropdown-backlinks-sidebar',
      }}
      title={
        <div className="sidebar-backlinks-header">
          <div className="sidebar-backlinks-header__title">Change request</div>
          <div className="sidebar-backlinks-header__info">
            <span className="sidebar-backlinks-header__info-subtitle">{data.websiteDomain}</span>
            {/* The change request's own status, not the order's. The order can be
                "Live" while its concern is still open, which read as a contradiction. */}
            {statusConfig && (
              <span
                className="sidebar-backlinks-header__info-lable"
                style={{
                  color: statusConfig.textColor,
                  backgroundColor: statusConfig.backgroundColor,
                }}
              >
                {statusConfig.title}
              </span>
            )}
          </div>
        </div>
      }
    >
      <div className="dropdown-backlinks-sidebar__content">
        <PlacementDetails data={data} />
        {renderBody()}
      </div>
    </RightInfoSidebar>
  );
}

export default ChangeRequestSidebar;
